import React, { useEffect, useMemo } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider, useTranslation } from "react-i18next";
import { FlareThemeProvider, useFlareTheme } from "flare-ui";
import { i18n, normalizeLocale } from "@/i18n";
import { useUIStore } from "@/app/state/ui-store";
import { AuthBootstrap } from "./auth-bootstrap";
import { CartBootstrap } from "./cart-bootstrap";
import { GlobalErrorDialogProvider } from "./global-error-dialog";
import { ThemeBootstrap } from "./theme-bootstrap";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false
        }
    }
});

/**
 * Application-wide infrastructure. Keep provider instances outside the
 * component so navigation and parent renders do not reset shared caches.
 */
function AppFlareThemeBridge() {
    const appearance = useUIStore(state => state.appearance);
    const { setTheme } = useFlareTheme();

    const themePatch = useMemo(() => ({
        mode: (appearance === "dark" ? "dark" : "light") as "dark" | "light",
        semantic: {
            bg: "var(--color-page)",
            bgSubtle: "var(--color-page)",
            surface: "var(--color-surface)",
            surfaceSubtle: "var(--color-surface)",
            surfaceElevated: "var(--color-surface-elevated)",
            fg: "var(--color-text)",
            fgMuted: "var(--color-text-secondary)",
            border: "var(--color-border)",
            borderSubtle: "var(--color-border)",
            accent: "var(--color-primary)",
            accentHover: "var(--color-primary)",
            accentActive: "var(--color-primary)",
            success: "var(--color-success)",
            warning: "var(--color-warning)",
            danger: "var(--color-danger)",
            error: "var(--color-danger)",
            errorSubtle: "color-mix(in srgb, var(--color-danger) 18%, var(--color-surface))",
            errorDark: "var(--color-danger)"
        }
    }), [appearance]);

    useEffect(() => {
        setTheme(themePatch);
    }, [setTheme, themePatch]);

    return null;
}

function AppFlareThemeProvider({ children }: { children: React.ReactNode }) {
    const { i18n } = useTranslation();
    const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language);

    return (
        <FlareThemeProvider locale={locale}>
            <AppFlareThemeBridge />
            <GlobalErrorDialogProvider queryClient={queryClient}>
                <AuthBootstrap />
                <CartBootstrap />
                <ThemeBootstrap />
                {children}
            </GlobalErrorDialogProvider>
        </FlareThemeProvider>
    );
}

export function AppProviders({ children }: { children: React.ReactNode }) {
    return (
        <I18nextProvider i18n={i18n}>
            <QueryClientProvider client={queryClient}>
                {/* Strip Vite's trailing BASE_URL slash for BrowserRouter's
                    root basename. */}
                <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "") || "/"}>
                    <AppFlareThemeProvider>{children}</AppFlareThemeProvider>
                </BrowserRouter>
            </QueryClientProvider>
        </I18nextProvider>
    );
}
